import React from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import ReceiptIcon from '@mui/icons-material/Receipt';
import { formatCurrency } from '../../utils/formatters';

const SectionHeader = ({ title }) => (
  <Box display="flex" alignItems="center" mb={2}>
    <ReceiptIcon color="primary" sx={{ mr: 1 }} />
    <Typography variant="h6">{title}</Typography>
  </Box>
);

const InvoiceRow = ({ label, valueBRL, valueUSD }) => (
  <TableRow>
    <TableCell component="th" scope="row">
      {label}
    </TableCell>
    <TableCell align="right">{formatCurrency(valueBRL, 'BRL')}</TableCell>
    <TableCell align="right">{formatCurrency(valueUSD, 'USD')}</TableCell>
  </TableRow>
);

/**
 * DistributionSaleInvoiceSection - Mirrors the Capa Rel. Imp.
 * "NOTA FISCAL DE VENDA" block (rows 44–51). Product Cost corresponds to
 * workbook cell J45.
 *
 * Rates are intentionally absent: ICMS Saída is per-product (4% / 12% / 17%),
 * so a single cover rate is misleading. Per-line rates live in the product
 * calculations table (icms_rate). This card shows totals/values only.
 */
const DistributionSaleInvoiceSection = ({ order }) => {
  if (!order) return null;

  const taxCalculation = order.latest_tax_calculation || {};

  const rows = [
    {
      label: 'Product Cost',
      brl: taxCalculation.custo_total_brl,
      usd: taxCalculation.custo_total_usd,
      visible: true,
    },
    {
      label: 'ICMS Tax Base',
      brl: taxCalculation.base_calculo_saida_brl,
      usd: taxCalculation.base_calculo_saida_usd,
      visible: true,
    },
    {
      label: 'ICMS Highlight',
      brl: taxCalculation.icms_nf_destaque_brl,
      usd: taxCalculation.icms_nf_destaque_usd,
      visible: true,
    },
    {
      label: 'IPI Highlight',
      brl: taxCalculation.ipi_saida_destaque_brl,
      usd: taxCalculation.ipi_saida_destaque_usd,
      visible: taxCalculation.ipi_saida_destaque_brl > 0,
    },
    {
      label: 'ICMS-ST Highlight',
      brl: taxCalculation.icms_st_destaque_nf_brl,
      usd: taxCalculation.icms_st_destaque_nf_usd,
      visible: taxCalculation.icms_st_destaque_nf_brl > 0,
    },
    {
      label: 'PIS',
      brl: taxCalculation.pis_saida_brl,
      usd: taxCalculation.pis_saida_usd,
      visible: true,
    },
    {
      label: 'COFINS',
      brl: taxCalculation.cofins_saida_brl,
      usd: taxCalculation.cofins_saida_usd,
      visible: true,
    },
  ];

  const visibleRows = rows.filter((row) => row.visible);

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <SectionHeader title="SALE INVOICE" />
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Description</TableCell>
                <TableCell align="right">BRL</TableCell>
                <TableCell align="right">USD</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleRows.map((row) => (
                <InvoiceRow
                  key={row.label}
                  label={row.label}
                  valueBRL={row.brl}
                  valueUSD={row.usd}
                />
              ))}
              <TableRow sx={{ backgroundColor: 'rgba(0, 0, 0, 0.04)' }}>
                <TableCell component="th" scope="row">
                  <Typography fontWeight="bold">Invoice Total</Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography fontWeight="bold">{formatCurrency(taxCalculation.nota_fiscal_brl)}</Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography fontWeight="bold">{formatCurrency(taxCalculation.nota_fiscal_usd, 'USD')}</Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
};

export default DistributionSaleInvoiceSection;
